import React, { useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Constants from "../../constants";
import HttpManager from "../../services/HttpManager";
import { getErrorMessage } from "../../common";
import { StudentAuditEntryDTO } from "../../models/EnrollmentHistoryDTO";
import { formatLocalDateTime, horaDoce } from "../../components/enrollment/format";
import { horarioBonito } from "../../utils/horarioBonito";

export interface StudentAuditLogTabProps {
	studentId: number;
	campusId: number;
	/** Bump to force a reload (e.g. after the profile was saved). */
	refreshKey?: number;
}

/*
	La etiqueta dice DE QUE es la entrada. Los pagos, las compras y las notas de
	matricula caian todos en el "default" y salian marcados como "Información de
	Perfil", que no es lo que son. (Esteban, 2026-09-21)
*/
const entityBadge = (entry: StudentAuditEntryDTO): { bg: string; text?: string; label: string } => {
	switch (entry.entityType) {
		case "enrollment": return { bg: "primary", label: Constants.Common.enrollments };
		//La solicitud de matrícula es su propia cosa: no es la matrícula ni el perfil.
		case "request": return { bg: "info", label: Constants.Common.requestTag };
		case "enrollment_note": return { bg: "primary", label: Constants.Common.enrollmentNote };
		case "document": return { bg: "info", text: "dark", label: Constants.Common.enrollmentDocuments };
		case "payment": return entry.action === "payment_cancelled"
			? { bg: "oro", label: Constants.Common.paymentVoided }
			: { bg: "success", label: Constants.Common.paymentTag };
		case "purchase": return { bg: "info", label: Constants.Common.purchase };
		default: return { bg: "secondary", label: Constants.Common.profileInfo };
	}
};

/*
	Las entradas guardan el nombre del horario tal como se capturó ("Domingo 16:00 - 19:30
	[SCVQGN33QGP]"). Se muestra en am/pm y con el código marcado como horario, igual que en la
	tarjeta de Matrículas. Solo cambia cómo se lee: el texto guardado no se toca (2026-09-23).
*/
const resumenLegible = (texto: string | null | undefined): string =>
	String(texto || "").replace(/·\s*([^·\[\n]+?)\s*\[([A-Z0-9]+)\]/g,
		(_todo, horario: string, codigo: string) => "· " + horarioBonito(horario.trim(), { separador: " ", compacto: true }) + " [Horario " + codigo + "]");

const dayOf = (value: string | null): string => value ? formatLocalDateTime(value.split("T")[0]) : "-";

/**
 * "Historial de Perfil": read-only, append-only log of everything that happened to the student —
 * who did it, what changed (before/after) and when. Grouped by day, newest first.
 */
const StudentAuditLogTab: React.FC<StudentAuditLogTabProps> = ({ studentId, campusId, refreshKey }) => {
	const http = HttpManager.getInstance();
	const [entries, setEntries] = useState<StudentAuditEntryDTO[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [expanded, setExpanded] = useState<{ [id: number]: boolean }>({});

	useEffect(() => {
		let cancelled = false;
		setError(null);
		http.studentService.getStudentAuditLog(campusId, studentId)
			.then((response) => { if (!cancelled) { setEntries(response.data); } })
			.catch((e) => { if (!cancelled) { setEntries([]); setError(getErrorMessage(e)); } });
		return () => { cancelled = true; };
	}, [campusId, studentId, refreshKey]);

	const groups: { day: string; items: StudentAuditEntryDTO[] }[] = [];
	(entries || []).forEach((entry) => {
		const day = dayOf(entry.createdAt);
		const last = groups[groups.length - 1];
		if (last && last.day === day) {
			last.items.push(entry);
		} else {
			groups.push({ day, items: [entry] });
		}
	});

	return (
		<div style={{ padding: "10px 5px", textAlign: "left" }}>
			<h6 style={{ margin: "0 0 6px" }}>{Constants.Common.profileHistory}</h6>
			{error ? <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div> : ""}
			{entries === null ? <div className="loader" /> : (entries.length < 1
				? <div style={{ padding: 15, color: "var(--wa-apagado)" }}>{Constants.Common.profileHistoryEmpty}</div>
				: groups.map((group) => (
					<div key={"day_" + group.day} style={{ marginBottom: 10 }}>
						<div style={{ fontSize: 12, color: "var(--wa-apagado)", textTransform: "uppercase", letterSpacing: ".04em", margin: "6px 0 4px" }}>{group.day}</div>
						<ul style={{ listStyle: "none", padding: 0, margin: 0, borderLeft: "2px solid var(--wa-acento-suave)" }}>
							{group.items.map((entry) => {
								const badge = entityBadge(entry);
								const hasChanges = entry.changes && entry.changes.length > 0;
								const open = !!expanded[entry.id];
								return (
									<li key={"audit_" + entry.id} style={{ padding: "5px 0 7px 12px", position: "relative", fontSize: 14 }}>
										<span style={{ position: "absolute", left: -6, top: 12, width: 10, height: 10, borderRadius: "50%", background: "var(--wa-tenue)" }} />
										<div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
											<span style={{ color: "var(--wa-apagado)", fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{horaDoce((entry.createdAt || "").split("T")[1] || "")}</span>
											<Badge bg={badge.bg} text={badge.text as any}>{badge.label}</Badge>
											<b>{entry.createdBy}</b>
										</div>
										<div style={{ marginTop: 2, wordBreak: "break-word" }}>
											{resumenLegible(entry.summary)}
											{hasChanges ? (
												<button type="button" className="btn btn-link btn-sm" style={{ padding: "0 6px", fontSize: 12 }}
													onClick={() => setExpanded({ ...expanded, [entry.id]: !open })}>
													<FontAwesomeIcon icon={open ? solid("chevron-up") : solid("chevron-down")} />&nbsp;{open ? Constants.Common.showLess : Constants.Common.showDetail}
												</button>
											) : ""}
										</div>
										{hasChanges && open ? (
											<table className="table table-sm" style={{ fontSize: 13, marginTop: 6, marginBottom: 0, maxWidth: 720 }}>
												<thead>
													<tr>
														<th style={{ width: "26%" }}>{Constants.Common.changeField}</th>
														<th style={{ width: "37%" }}>{Constants.Common.changeBefore}</th>
														<th style={{ width: "37%" }}>{Constants.Common.changeAfter}</th>
													</tr>
												</thead>
												<tbody>
													{entry.changes.map((change, index) => (
														<tr key={entry.id + "_" + index}>
															<td>{change.label}</td>
															<td style={{ color: "var(--wa-apagado)", wordBreak: "break-word" }}>{change.old ?? "-"}</td>
															<td style={{ wordBreak: "break-word" }}>{change.new ?? "-"}</td>
														</tr>
													))}
												</tbody>
											</table>
										) : ""}
									</li>
								);
							})}
						</ul>
					</div>
				)))}
		</div>
	);
};

export default StudentAuditLogTab;
